import { useEffect } from 'react';
import { LAYERS } from '@/utils/layers';

// CSS Variables mapping for validation
const CSS_VARIABLES_MAP = {
  '--z-header-footer': LAYERS.HEADER_FOOTER,
  '--z-modal': LAYERS.DIALOG_CONTENT,
  '--z-toast': LAYERS.TOAST,
  '--z-tooltip': LAYERS.AUDIO,
} as const;

/**
 * Development hook to validate z-index usage and detect potential conflicts
 * Only runs in development mode to avoid performance impact in production
 */
export function useZIndexValidation() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    
    // Only run if explicitly enabled via localStorage
    const isEnabled = localStorage.getItem('enableZIndexValidation') === 'true';
    if (!isEnabled) {
      console.log('💡 Z-Index validation disabled. Enable with: localStorage.setItem("enableZIndexValidation", "true")');
      return;
    }

    let lastValidationTime = 0;
    const THROTTLE_INTERVAL = 30000; // 30 seconds minimum between validations

    const validateZIndexUsage = () => {
      const now = Date.now();
      if (now - lastValidationTime < THROTTLE_INTERVAL) {
        return; // Throttle to prevent spam
      }
      lastValidationTime = now;
      const issues: string[] = [];
      
      // Find all elements with z-index
      const elementsWithZIndex = Array.from(document.querySelectorAll('*')).filter(el => {
        const styles = getComputedStyle(el);
        return styles.zIndex !== 'auto' && styles.zIndex !== '0';
      });

      // Check for magic numbers (not using our token system)
      const validZIndexValues = new Set(Object.values(LAYERS).map(String));
      
      elementsWithZIndex.forEach(el => {
        const zIndex = getComputedStyle(el).zIndex;
        
        // Check for magic numbers
        if (!validZIndexValues.has(zIndex) && !zIndex.startsWith('var(')) {
          issues.push(`Element has magic z-index value: ${zIndex} (${el.tagName}${el.className ? '.' + el.className.split(' ')[0] : ''})`);
        }

        // Check for extremely high values (likely problematic)
        if (parseInt(zIndex) > 200) {
          issues.push(`Element has suspiciously high z-index: ${zIndex} (${el.tagName}${el.className ? '.' + el.className.split(' ')[0] : ''})`);
        }

        // Check for negative values (unusual, might be problematic)
        if (parseInt(zIndex) < 0) {
          issues.push(`Element has negative z-index: ${zIndex} (${el.tagName}${el.className ? '.' + el.className.split(' ')[0] : ''})`);
        }
      });

      // Validate CSS variables alignment with LAYERS constants
      const rootStyles = getComputedStyle(document.documentElement);
      Object.entries(CSS_VARIABLES_MAP).forEach(([cssVar, expectedValue]) => {
        const actualValue = parseInt(rootStyles.getPropertyValue(cssVar), 10);
        if (actualValue !== expectedValue) {
          issues.push(`🚨 Z-Index System Mismatch: CSS variable ${cssVar} is ${actualValue}, expected ${expectedValue} (LAYERS constant)`);
        }
      });

      // Check for potential stacking context issues
      elementsWithZIndex.forEach(el => {
        const styles = getComputedStyle(el);
        const hasStackingContext = 
          styles.position !== 'static' || 
          styles.transform !== 'none' ||
          styles.opacity !== '1' ||
          styles.isolation === 'isolate' ||
          parseFloat(styles.opacity) < 1;

        if (!hasStackingContext && parseInt(styles.zIndex) > 0) {
          issues.push(`Element has z-index but no stacking context: ${styles.zIndex} (${el.tagName}${el.className ? '.' + el.className.split(' ')[0] : ''})`);
        }
      });

      // Only log if there are actual issues
      if (issues.length > 0) {
        console.group('🎭 Z-Index Validation Issues');
        issues.forEach(issue => console.warn(issue));
        console.groupEnd();
      }
    };

    // Use requestIdleCallback for better performance, fallback to setTimeout
    const scheduleValidation = (callback: () => void, delay: number) => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(callback, { timeout: delay });
      } else {
        setTimeout(callback, delay);
      }
    };

    // Run validation after components render (much longer delay)
    const timeoutId = setTimeout(() => {
      scheduleValidation(validateZIndexUsage, 5000);
    }, 5000);
    
    // Also run on DOM changes (much longer debounce - 50 seconds)
    let validationTimeout: NodeJS.Timeout;
    const observer = new MutationObserver(() => {
      clearTimeout(validationTimeout);
      validationTimeout = setTimeout(() => {
        scheduleValidation(validateZIndexUsage, 50000);
      }, 50000);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(validationTimeout);
      observer.disconnect();
    };
  }, []);
}

/**
 * Utility function to check if an element is properly layered
 * @param element - The DOM element to check
 * @param expectedLayer - The expected layer name from LAYERS
 */
export function validateElementLayer(element: HTMLElement, expectedLayer: keyof typeof LAYERS): boolean {
  const actualZIndex = getComputedStyle(element).zIndex;
  const expectedZIndex = LAYERS[expectedLayer].toString();
  
  if (actualZIndex !== expectedZIndex) {
    console.warn(`Layer mismatch: expected ${expectedLayer} (${expectedZIndex}), got ${actualZIndex}`, element);
    return false;
  }
  
  return true;
}

/**
 * Development helper to highlight elements by z-index layer
 * @param layerName - The layer to highlight
 */
export function highlightLayer(layerName: keyof typeof LAYERS) {
  if (process.env.NODE_ENV !== 'development') return;
  
  const targetZIndex = LAYERS[layerName].toString();
  
  // Remove existing highlights
  document.querySelectorAll('.z-index-highlight').forEach(el => {
    el.classList.remove('z-index-highlight');
  });
  
  // Add highlights to matching elements
  document.querySelectorAll('*').forEach(el => {
    const zIndex = getComputedStyle(el).zIndex;
    if (zIndex === targetZIndex) {
      el.classList.add('z-index-highlight');
    }
  });
  
  // Add CSS for highlighting if not already present
  if (!document.getElementById('z-index-highlight-styles')) {
    const style = document.createElement('style');
    style.id = 'z-index-highlight-styles';
    style.textContent = `
      .z-index-highlight {
        outline: 3px solid red !important;
        outline-offset: 2px !important;
        position: relative !important;
      }
      .z-index-highlight::before {
        content: "${layerName} (${targetZIndex})";
        position: absolute;
        top: -20px;
        left: 0;
        background: red;
        color: white;
        padding: 2px 6px;
        font-size: 12px;
        font-family: monospace;
        z-index: 9999;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
  
  console.log(`Highlighted ${layerName} layer (z-index: ${targetZIndex})`);
}

/**
 * Clear all layer highlights
 */
export function clearLayerHighlights() {
  document.querySelectorAll('.z-index-highlight').forEach(el => {
    el.classList.remove('z-index-highlight');
  });
  
  const style = document.getElementById('z-index-highlight-styles');
  if (style) {
    style.remove();
  }
}